import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { Receipt } from 'lucide-react';
import './CreateGroupEvent.css';

function generateEventCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return 'XPM-' + Array.from({ length: 4 }, () =>
    chars[Math.floor(Math.random() * chars.length)]
  ).join('');
}

export default function CreateGroupEvent({ user }: { user: User }) {
  const [name, setName] = useState('');
  const [venue, setVenue] = useState('');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState(new Date().toISOString().split('T')[0]);
  const [totalMembers, setTotalMembers] = useState('');
  const [paymentHandle, setPaymentHandle] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const canSubmit = name.trim() && eventDate && parseInt(totalMembers) >= 2 && !saving;

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError('');

    const id = crypto.randomUUID();
    const eventCode = generateEventCode();
    const now = Date.now();

    const event = {
      id,
      name: name.trim(),
      description: description.trim(),
      venue: venue.trim(),
      eventDate: new Date(eventDate + 'T12:00:00').getTime(),
      createdAt: now,
      updatedAt: now,
      adminId: user.uid,
      userId: user.uid,
      eventCode,
      totalMembers: parseInt(totalMembers),
      contributors: [],
      paymentHandle: paymentHandle.trim(),
      settlementStatuses: {},
    };

    try {
      await setDoc(doc(db, 'users', user.uid, 'events', id), event);
      navigate(`/admin/event/${eventCode}`);
    } catch (err) {
      console.error(err);
      setError('Failed to create event. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="ce-page">
      <header className="ce-header">
        <button className="ce-back" onClick={() => navigate('/')}>← Back</button>
        <div className="ce-logo">
          <div className="ce-logo-icon">
            <Receipt size={18} color="white" strokeWidth={2} />
          </div>
          <span className="ce-logo-text">XpenseMate</span>
        </div>
      </header>

      <main className="ce-main">
        <h1 className="ce-title">New Group Event</h1>
        <p className="ce-subtitle">
          Fill in the details — you'll get a shareable link to send your group.
        </p>

        <form className="ce-form" onSubmit={handleCreate} noValidate>
          <div className="ce-field">
            <label>Event name <span className="ce-req">*</span></label>
            <input
              type="text"
              placeholder="e.g. Vegas Trip, Team Dinner, Ski Weekend"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="ce-field">
            <label>Venue</label>
            <input
              type="text"
              placeholder="e.g. Nobu NYC"
              value={venue}
              onChange={e => setVenue(e.target.value)}
            />
          </div>

          <div className="ce-row">
            <div className="ce-field">
              <label>Date <span className="ce-req">*</span></label>
              <input
                type="date"
                value={eventDate}
                onChange={e => setEventDate(e.target.value)}
              />
            </div>
            <div className="ce-field">
              <label>
                # of members <span className="ce-req">*</span>
                <span className="ce-field-hint"> (split denominator)</span>
              </label>
              <input
                type="number"
                min="2"
                max="200"
                placeholder="e.g. 6"
                value={totalMembers}
                onChange={e => setTotalMembers(e.target.value)}
              />
            </div>
          </div>

          <div className="ce-field">
            <label>Note <span className="ce-optional">(optional)</span></label>
            <input
              type="text"
              placeholder="Any extra info for your group"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          <div className="ce-field">
            <label>Your payment handle <span className="ce-optional">(for settlement)</span></label>
            <input
              type="text"
              placeholder="@venmo, $cashapp, or paypal.me/you"
              value={paymentHandle}
              onChange={e => setPaymentHandle(e.target.value)}
            />
          </div>

          <div className="ce-info-box">
            <span className="ce-info-icon">ℹ</span>
            <span>
              <strong># of members</strong> is the denominator for splitting costs.
              Per-person share = total group expenses ÷ this number, even if someone hasn't submitted yet.
            </span>
          </div>

          {error && <p className="ce-error">{error}</p>}

          <button type="submit" className="ce-btn-submit" disabled={!canSubmit}>
            {saving ? 'Creating…' : 'Create Event →'}
          </button>
        </form>
      </main>
    </div>
  );
}
